// Router
import { useNavigate, useParams } from "@/lib/router"
import { Routes } from "@/constants/routes"

// Queries
import { ProjectTypeEnum, useSingleProjectStatsQuery } from "@/api/graphql"

// UI
import { Card } from "@/components/Card"
import { InternalStateOfReturnGraph } from "../_components/InternalStateOfReturnGraph"
import { ProjectInterestPeriodsTable } from "./_components/ProjectInterestPeriodsTable"
import { ProjectInterestByPeriodGraph } from "./_components/ProjectInterestByPeriodGraph"
import { PageTemplate } from "@/layouts/PageTemplate"
import { ProjectSelector } from "@/components/ProjectSelector"
import { InvestmentOpportunityBanner } from "@/components/banners/InvestmentOpportunityBanner"
import { GraphLabel, GraphLabels } from "@/components/graphs/GraphLabels"
import { Heading } from "@/components/Typography"
import { SolarInterestTariffGraph } from "@/components/graphs/types/area/SolarInterestTariffGraph"
import { SolarInterestTariffDownloads } from "@/pages/finance/solar-interest-tariff/_components/SolarInterestTariffDownloads"

// Components
import { SingleProjectStatsInternalRateOfReturn } from "../_components/SingleProjectStatsInternalRateOfReturn"

// I18n
import { useTrans } from "@/i18n"
import { useLang } from "@/context/useLang"

// Utils
import { cn } from "@/lib/utils"

// Context
import { useFeatureFlags } from "@/context/user"
import { AuthStateStatusEnum, useAuth } from "@/context/useAuth"

// Graphs
import { GlobalSolarInterestTariffGraph } from "./_components/GlobalSolarInterestTariffGraph"

// Contants
const ENABLE_SOLAR_TARIFF_GRAPH = false
const ENABLE_IRR_GRAPH = false

function LoadingValue({ className }: { className?: string }) {
	return (
		<span
			className={cn(
				"inline-block h-4 w-24 animate-pulse rounded bg-gray-50 align-middle",
				className,
			)}
		/>
	)
}

/**
 * SingleProjectStats
 * @param param0
 * @returns
 */
export function SingleProjectStats() {
	// I18n
	const t = useTrans("dashboard")
	const { formatNumber } = useLang()

	// Context
	const { getFeatureFlagValue } = useFeatureFlags()
	const auth = useAuth()

	// Router
	const navigate = useNavigate()
	const params = useParams()
	const projectId = Number(params.id)

	// Fetch
	const { data } = useSingleProjectStatsQuery({
		id: String(params.id),
	})
	const project = data?.project
	const isLoading = project == null

	return (
		<div className="my-5">
			<PageTemplate
				title={project?.name ?? ""}
				topRightComponent={
					<ProjectSelector
						projectId={projectId}
						onChange={(id) =>
							navigate(
								Routes.StatisticsPlatformSingleProject.replace(
									":id",
									id,
								),
							)
						}
					/>
				}
				backHref={Routes.StatisticsProjects}
			>
				{auth.status === AuthStateStatusEnum.LOGGED_IN ? (
					<InvestmentOpportunityBanner className="mb-4" />
				) : null}
				<div className="grid grid-cols-1 gap-8 md:col-span-4 lg:grid-cols-2 xl:col-span-3">
					{data?.project?.type === ProjectTypeEnum.Portfolio &&
						getFeatureFlagValue("ENABLE_DISPLAY_IRR") === true && (
							<SingleProjectStatsInternalRateOfReturn
								projectId={projectId}
							/>
						)}

					<Card className="h-full group-hover:bg-gray-50 md:col-span-1 xl:col-span-1">
						<dl className="grid grid-cols-[minmax(0,auto)_1fr] gap-x-4 gap-y-2 text-sm">
							{/** Project type */}
							<dt className="text-gray-600">
								{t(
									"dashboard:stats.project_stats.info.project_type",
								)}
							</dt>
							<dd className="text-right font-semibold text-gray-900 sm:text-left">
								{project?.type ? (
									t(`common:project.type.${project.type}`)
								) : (
									<LoadingValue />
								)}
							</dd>

							{/** Project interest_type */}
							<dt className="text-gray-600">
								{t(
									"dashboard:stats.project_stats.info.interest_type",
								)}
							</dt>
							<dd className="text-right font-semibold text-gray-900 sm:text-left">
								{project?.interest_payment_calculation_type ? (
									t(
										`dashboard:stats.project_stats.interest_calculation_types.${project.interest_payment_calculation_type}`,
									)
								) : (
									<LoadingValue />
								)}
							</dd>

							{/** Project start */}
							<dt className="text-gray-600">
								{t(
									"dashboard:stats.project_stats.info.start_date",
								)}
							</dt>
							<dd className="text-right font-semibold text-gray-900 sm:text-left">
								{project?.start ?? <LoadingValue />}
							</dd>

							{/** Energy supplier — only shown once we know whether
							 * the project has a supplier. */}
							{isLoading ? (
								<>
									<dt className="text-gray-600">
										{t(
											"dashboard:stats.project_stats.info.energy_supplier",
										)}
									</dt>
									<dd className="text-right font-semibold text-gray-900 sm:text-left">
										<LoadingValue />
									</dd>
								</>
							) : project?.supplier ? (
								<>
									<dt className="text-gray-600">
										{t(
											"dashboard:stats.project_stats.info.energy_supplier",
										)}
									</dt>
									<dd className="text-right font-semibold text-gray-900 sm:text-left">
										{project.supplier.name}
									</dd>
								</>
							) : null}

							<dt className="text-gray-600">
								{t(
									"dashboard:stats.project_stats.info.financial_structure",
								)}
							</dt>
							<dd className="text-right font-semibold text-gray-900 sm:text-left">
								{project?.amortization_schedule ?? (
									<LoadingValue />
								)}
							</dd>

							{/** Maturity */}
							<dt className="text-gray-600">
								{t(
									"dashboard:stats.project_stats.info.maturity",
								)}
							</dt>
							<dd className="text-right font-semibold text-gray-900 sm:text-left">
								{project?.duration != null ? (
									<>
										{project.duration}{" "}
										{t(
											"dashboard:stats.project_stats.info.years",
										)}
									</>
								) : (
									<LoadingValue />
								)}
							</dd>

							{/** Total shares / bonds issued */}
							<dt className="text-gray-600">
								{t("dashboard:total_shares")}
							</dt>
							<dd className="text-right font-semibold text-gray-900 sm:text-left">
								{project?.total_shares != null ? (
									formatNumber(project.total_shares)
								) : (
									<LoadingValue className="w-12" />
								)}
							</dd>
						</dl>
					</Card>
				</div>

				{/** Show graph when project type is solarShares */}
				{ENABLE_IRR_GRAPH ? <InternalStateOfReturnGraph /> : null}

				{/** Interest periods table */}
				<ProjectInterestPeriodsTable />

				{/** Expected vs actual interest graph */}
				<ProjectInterestByPeriodGraph />

				{/* For SolarShares projects show interest tariffs*/}
				{data?.project?.type === ProjectTypeEnum.SolarShares ? (
					<>
						{auth.status === AuthStateStatusEnum.LOGGED_IN ? (
							<SolarInterestTariffDownloads />
						) : null}

						{/** Endex graph */}
						{/** @ts-expect-error Temp var */}
						{ENABLE_SOLAR_TARIFF_GRAPH === true ? (
							<Card>
								<Heading
									as="h3"
									styleAs="h6"
									className="mr-2 lg:mr-0"
								>
									{t(
										"finance:solar_interest_tariff.block.endex_tariffs_full_year.title",
									)}
								</Heading>

								<SolarInterestTariffGraph>
									<SolarInterestTariffGraph.Content>
										<GlobalSolarInterestTariffGraph />
									</SolarInterestTariffGraph.Content>
									<SolarInterestTariffGraph.Footer>
										<GraphLabels className="p-3 px-4 sm:p-5 sm:px-6">
											<GraphLabel
												type="line"
												variant="primary"
												title={t(
													"finance:solar_interest_tariff.block.endex_tariffs",
												)}
											/>
										</GraphLabels>
									</SolarInterestTariffGraph.Footer>
								</SolarInterestTariffGraph>
							</Card>
						) : null}
					</>
				) : null}
			</PageTemplate>
		</div>
	)
}
